/**
 * Browser-based ROI Table Generator
 * Uses docx library to generate ROI tables (ported from Lambda)
 */

import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, ShadingType } from 'docx';

/**
 * ROI Calculation Helper Functions
 */

function parseTimeToSeconds(timeStr) {
  if (!timeStr) return 0;
  const numMatch = timeStr.match(/[\d.]+/);
  if (!numMatch) return 0;
  const value = parseFloat(numMatch[0]);
  const lowerStr = timeStr.toLowerCase();
  if (lowerStr.includes('hour') || lowerStr.includes('hr') || lowerStr.includes('h')) {
    return value * 3600;
  } else if (lowerStr.includes('min') || lowerStr.includes('m')) {
    return value * 60;
  } else if (lowerStr.includes('sec') || lowerStr.includes('s')) {
    return value;
  }
  return value;
}

function calculateAutomationWithProbability(probability, automationPotential) {
  if (!probability || !automationPotential) return '0%';
  const probValue = parseFloat(probability) / 100;
  const autoValue = parseFloat(automationPotential) / 100;
  const result = probValue * autoValue * 100;
  return `${result.toFixed(1)}%`;
}

function calculateTotalInteractions(numberOfInteractions, automationWProbability) {
  if (!numberOfInteractions || !automationWProbability) return '0';
  const interactions = parseFloat(numberOfInteractions);
  const percentage = parseFloat(automationWProbability) / 100;
  const result = interactions * percentage;
  return result.toFixed(0);
}

function calculateTotalTimeSaved(totalInteractions, avgTimePerInteraction) {
  if (!totalInteractions || !avgTimePerInteraction) return '0';
  const interactions = parseFloat(totalInteractions);
  const timeInSeconds = parseTimeToSeconds(avgTimePerInteraction);
  const result = interactions * timeInSeconds;
  return result.toFixed(0);
}

function calculateSavingsPerPeriod(totalTimeSaved, costPerAgent) {
  if (!totalTimeSaved || !costPerAgent) return '$0.00';
  const timeInHours = parseFloat(totalTimeSaved) / 3600;
  const savings = timeInHours * costPerAgent;
  return `$${savings.toFixed(2)}`;
}

/**
 * Create ROI table for a single process
 */
function createProcessROITable(process, costPerAgent) {
  const cases = ['Conservative', 'Likely', 'Optimistic'];
  const defaultProbabilities = {
    'Conservative': '65%',
    'Likely': '80%',
    'Optimistic': '95%'
  };
  const defaultValues = {
    automation_area: 'Self-service',
    automation_potential: '80%',
    number_of_interactions: '10000',
    avg_time_per_interaction: '30 sec'
  };

  const elements = [];

  // Get process-level values
  const firstCaseData = process.roi_cases?.Conservative || process.roi_cases?.Likely || process.roi_cases?.Optimistic || {};
  const automationArea = firstCaseData.automation_area || defaultValues.automation_area;
  const automationPotential = firstCaseData.automation_potential || defaultValues.automation_potential;
  const numberOfInteractions = firstCaseData.number_of_interactions || defaultValues.number_of_interactions;
  const avgTimePerInteraction = firstCaseData.avg_time_per_interaction || defaultValues.avg_time_per_interaction;

  // Process Information Table
  const infoRows = [
    new TableRow({
      children: [
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: "Process ID", bold: true })] })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
          width: { size: 30, type: WidthType.PERCENTAGE }
        }),
        new TableCell({
          children: [new Paragraph(process.id)],
          width: { size: 70, type: WidthType.PERCENTAGE }
        })
      ]
    }),
    new TableRow({
      children: [
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: "Process Name", bold: true })] })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
        }),
        new TableCell({
          children: [new Paragraph(process.name)]
        })
      ]
    }),
    new TableRow({
      children: [
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: "Automation Area", bold: true })] })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
        }),
        new TableCell({
          children: [new Paragraph(automationArea)]
        })
      ]
    }),
    new TableRow({
      children: [
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: "Automation Potential", bold: true })] })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
        }),
        new TableCell({
          children: [new Paragraph(automationPotential)]
        })
      ]
    })
  ];

  const infoTable = new Table({
    rows: infoRows,
    width: { size: 100, type: WidthType.PERCENTAGE }
  });

  elements.push(infoTable);
  elements.push(new Paragraph({ text: "" }));

  // Case-based ROI Table
  const caseRows = [];

  // Header row
  caseRows.push(
    new TableRow({
      tableHeader: true,
      children: [
        new TableCell({
          children: [new Paragraph({ text: "Case", alignment: AlignmentType.CENTER })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
        }),
        new TableCell({
          children: [new Paragraph({ text: "Probability", alignment: AlignmentType.CENTER })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
        }),
        new TableCell({
          children: [new Paragraph({ text: "Automation w/ Probability", alignment: AlignmentType.CENTER })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
        }),
        new TableCell({
          children: [new Paragraph({ text: "Number of Interactions", alignment: AlignmentType.CENTER })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
        }),
        new TableCell({
          children: [new Paragraph({ text: "Total Interactions", alignment: AlignmentType.CENTER })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
        }),
        new TableCell({
          children: [new Paragraph({ text: "Avg Time", alignment: AlignmentType.CENTER })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
        }),
        new TableCell({
          children: [new Paragraph({ text: "Total Time Saved", alignment: AlignmentType.CENTER })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
        }),
        new TableCell({
          children: [new Paragraph({ text: "Savings per Period", alignment: AlignmentType.CENTER })],
          shading: { fill: "D3D3D3", type: ShadingType.CLEAR },
        }),
      ],
    })
  );

  // Data rows for each case
  cases.forEach(caseType => {
    const caseData = process.roi_cases?.[caseType] || {};
    const probability = caseData.probability || defaultProbabilities[caseType];

    const automationWProbability = calculateAutomationWithProbability(probability, automationPotential);
    const totalInteractions = calculateTotalInteractions(numberOfInteractions, automationWProbability);
    const totalTimeSaved = calculateTotalTimeSaved(totalInteractions, avgTimePerInteraction);
    const savingsPerPeriod = calculateSavingsPerPeriod(totalTimeSaved, costPerAgent);

    caseRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ text: caseType, alignment: AlignmentType.CENTER })],
            shading: { fill: "F5F5F5", type: ShadingType.CLEAR },
          }),
          new TableCell({
            children: [new Paragraph({ text: probability, alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            children: [new Paragraph({ text: automationWProbability, alignment: AlignmentType.CENTER })],
            shading: { fill: "E6F3FF", type: ShadingType.CLEAR },
          }),
          new TableCell({
            children: [new Paragraph({ text: numberOfInteractions, alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            children: [new Paragraph({ text: totalInteractions, alignment: AlignmentType.CENTER })],
            shading: { fill: "E6FFE6", type: ShadingType.CLEAR },
          }),
          new TableCell({
            children: [new Paragraph({ text: avgTimePerInteraction, alignment: AlignmentType.CENTER })],
          }),
          new TableCell({
            children: [new Paragraph({ text: `${totalTimeSaved} sec`, alignment: AlignmentType.CENTER })],
            shading: { fill: "E6FFE6", type: ShadingType.CLEAR },
          }),
          new TableCell({
            children: [new Paragraph({ text: savingsPerPeriod, alignment: AlignmentType.CENTER })],
            shading: { fill: "FFF9E6", type: ShadingType.CLEAR },
          })
        ]
      })
    );
  });

  const caseTable = new Table({
    rows: caseRows,
    width: { size: 100, type: WidthType.PERCENTAGE }
  });

  elements.push(caseTable);

  return elements;
}

/**
 * Generate ROI Table as Word document (browser-based)
 */
export async function generateROITableDocument(processes, contextData) {
  console.log(`[roiTableGenerator] Generating ROI table for ${processes.length} processes`);

  const costPerAgent = contextData?.cost_per_agent || 25;

  // Create document elements for all processes
  const documentChildren = [
    new Paragraph({
      text: "ROI Analysis Table",
      heading: "Heading1",
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({ text: "" }),
  ];

  // Add table for each process
  processes.forEach((process, index) => {
    const processElements = createProcessROITable(process, costPerAgent);
    documentChildren.push(...processElements);

    if (index < processes.length - 1) {
      documentChildren.push(new Paragraph({ text: "" }));
      documentChildren.push(new Paragraph({ text: "" }));
    }
  });

  // Create document
  const doc = new Document({
    sections: [
      {
        properties: {},
        children: documentChildren,
      },
    ],
  });

  // Generate blob (browser-specific)
  const blob = await Packer.toBlob(doc);
  console.log('[roiTableGenerator] ROI table document generated successfully, blob size:', blob.size);

  return blob;
}
